import React, { useState } from 'react';
import { Guest, AttendanceStatus } from '../../_graphql/generated/types';

interface GuestDetailsModalProps {
  guest: Guest;
  eventCode?: string;
  hasInvitationTypesWithDesigns: boolean;
  onClose: () => void;
  onCreateInvitation: (guestId: string) => void;
  onViewInvitation: (guest: Guest) => void;
  onCopyInvitationLink: (guest: Guest) => void;
  onAddPlusOne: (guest: Guest) => void;
  onUninvite: (guest: Guest) => void;
  onDelete: (guestId: string, guestName?: string) => void;
  onUpdateGuest: (guestId: string, updates: { name?: string; email?: string; phone?: string }) => Promise<Guest>;
  copiedGuestId: string | null;
  viewCount?: number;
}

const GuestDetailsModal: React.FC<GuestDetailsModalProps> = ({ 
  guest, 
  hasInvitationTypesWithDesigns,
  onClose,
  onCreateInvitation,
  onViewInvitation,
  onCopyInvitationLink,
  onAddPlusOne,
  onUninvite,
  onDelete,
  onUpdateGuest,
  copiedGuestId,
  viewCount = 0
}) => {
  const [currentGuest, setCurrentGuest] = useState(guest);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(currentGuest.name || '');
  const [editedEmail, setEditedEmail] = useState(currentGuest.email || '');
  const [editedPhone, setEditedPhone] = useState(currentGuest.phone || '');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const getAttendanceDisplay = (status: AttendanceStatus) => {
    switch (status) {
      case AttendanceStatus.ATTENDING:
        return { text: 'Attending', className: 'bg-green-100 text-green-700' };
      case AttendanceStatus.NOT_ATTENDING:
        return { text: 'Not Attending', className: 'bg-red-100 text-red-700' };
      case AttendanceStatus.NO_RESPONSE:
        return { text: 'No Response', className: 'bg-gray-100 text-gray-700' };
      default:
        return { text: 'Unknown', className: 'bg-gray-100 text-gray-700' };
    }
  };

  const attendance = getAttendanceDisplay(currentGuest.attendance);

  const handleSave = async () => {
    if (!hasChanges) return;
    
    setIsSaving(true);
    try {
      const updatedGuest = await onUpdateGuest(currentGuest.id, {
        name: editedName.trim() || undefined,
        email: editedEmail.trim() || undefined,
        phone: editedPhone.trim() || undefined
      });
      
      // Update the local state with the new guest data
      setCurrentGuest(updatedGuest);
      setHasChanges(false);
      setIsEditingName(false);
    } catch (error) {
      console.error('Failed to update guest:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNameChange = (value: string) => {
    setEditedName(value);
    setHasChanges(true);
  };

  const handleEmailChange = (value: string) => {
    setEditedEmail(value);
    setHasChanges(true);
  };

  const handlePhoneChange = (value: string) => {
    setEditedPhone(value);
    setHasChanges(true);
  };

  const handleCancel = () => {
    setEditedName(currentGuest.name || '');
    setEditedEmail(currentGuest.email || '');
    setEditedPhone(currentGuest.phone || '');
    setHasChanges(false);
    setIsEditingName(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-4 w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1 mr-4">
            <div className="flex items-center gap-2">
              {currentGuest.primaryGuestId && (
                <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-gray-600 bg-gray-100 rounded-full">
                  +1
                </span>
              )}
              {isEditingName ? (
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  onBlur={() => setIsEditingName(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setIsEditingName(false);
                    } else if (e.key === 'Escape') {
                      setEditedName(currentGuest.name || '');
                      setIsEditingName(false);
                      setHasChanges(false);
                    }
                  }}
                  className="text-xl font-bold text-gray-900 border-b-2 border-blue-500 outline-none bg-transparent"
                  placeholder="Guest name"
                  autoFocus
                />
              ) : (
                <button
                  onClick={() => setIsEditingName(true)}
                  className="text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors flex items-center gap-1"
                >
                  {editedName || 'Guest'}
                  <svg className="w-4 h-4 text-gray-400 hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              )}
            </div>
            {currentGuest.primaryGuestId && (
              <p className="text-xs text-gray-500">Plus one guest</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Guest Details */}
        <div className="space-y-3">
          {/* Contact Information */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Contact Info</h3>
              <span className="text-xs text-gray-500">Guest #{currentGuest.guestNumber}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input
                  type="email"
                  value={editedEmail}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  placeholder="Email"
                  className="w-full px-2 py-1 text-sm bg-white border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <input
                  type="tel"
                  value={editedPhone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="Phone"
                  className="w-full px-2 py-1 text-sm bg-white border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Invitation Status */}
          <div className="bg-gray-50 rounded-lg p-3">
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Status</h3>
            <div className="flex items-center justify-between">
              <div className="text-sm">
                {currentGuest.invitationId ? (
                  <>
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${attendance.className}`}>
                      {attendance.text}
                    </span>
                    {currentGuest.invitation && (
                      <span className="text-xs text-gray-600 ml-2">
                        {currentGuest.invitation.groupName || 'Individual'} • {currentGuest.invitation.invitationType?.name || 'Standard'}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                    Not Invited
                  </span>
                )}
              </div>
            </div>
            {currentGuest.invitationId && viewCount > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                <span className="inline-flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Viewed {viewCount} time{viewCount !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>

          {/* RSVP Details */}
          {currentGuest.invitationId && currentGuest.attendance !== AttendanceStatus.NO_RESPONSE && (
            <div className="bg-gray-50 rounded-lg p-3">
              <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">RSVP Details</h3>
              <div className="space-y-2">
                {/* Dietary Requirements */}
                {(currentGuest.dietaryRequirements || currentGuest.attendance === AttendanceStatus.ATTENDING) && (
                  <div>
                    <span className="text-xs text-gray-600">Dietary:</span>
                    <div className="text-xs text-gray-900 mt-0.5">
                      {currentGuest.dietaryRequirements || <span className="text-gray-400 italic">None specified</span>}
                    </div>
                  </div>
                )}
                
                {/* Response Messages */}
                {currentGuest.invitation?.responses && currentGuest.invitation.responses.length > 0 && (
                  <div>
                    <span className="text-xs text-gray-600">Response Messages:</span>
                    <div className="mt-1 space-y-2">
                      {currentGuest.invitation.responses
                        .filter(response => response.message !== null && response.message !== undefined && response.message.trim() !== '')
                        .sort((a, b) => b.createdAt - a.createdAt) // Sort newest first
                        .map((response) => (
                          <div key={response.id} className="bg-white rounded border border-gray-200 p-2">
                            <div className="text-xs text-gray-900">{response.message}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {formatDate(new Date(response.createdAt).toISOString())}
                            </div>
                          </div>
                        ))}
                      {currentGuest.invitation.responses.filter(r => r.message !== null && r.message !== undefined && r.message.trim() !== '').length === 0 && (
                        <div className="text-xs text-gray-400 italic">No messages</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Action Buttons */}
        <div className="mt-4 space-y-2">
          <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Actions</h3>
          
          {!currentGuest.invitationId ? (
            // Actions for uninvited guests
            <div className="space-y-2">
              <button
                onClick={() => {
                  onCreateInvitation(currentGuest.id);
                  onClose();
                }}
                disabled={!hasInvitationTypesWithDesigns || hasChanges}
                className="w-full px-3 py-1.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                title={hasChanges ? "Save or cancel changes first" : !hasInvitationTypesWithDesigns ? "No invitation types with designs available" : "Create invitation for this guest"}
              >
                {!hasInvitationTypesWithDesigns ? 'No Designs Available' : 'Create Invitation Link'}
              </button>
              
              {!currentGuest.primaryGuestId && (
                <button
                  onClick={() => {
                    onAddPlusOne(currentGuest);
                    onClose();
                  }}
                  disabled={hasChanges}
                  className="w-full px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                  title={hasChanges ? "Save or cancel changes first" : "Add plus ones to this guest"}
                >
                  Add Plus Ones
                </button>
              )}
            </div>
          ) : (
            // Actions for invited guests
            <div className="space-y-2">
              <button
                onClick={() => onViewInvitation(currentGuest)}
                disabled={hasChanges}
                className="w-full px-3 py-1.5 bg-gradient-to-r from-blue-400 to-cyan-500 text-white rounded-lg hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm font-medium"
                title={hasChanges ? "Save or cancel changes first" : "View invitation as guest"}
              >
                View Invitation
              </button>
              
              <button
                onClick={() => onCopyInvitationLink(currentGuest)}
                disabled={hasChanges}
                className={`w-full px-4 py-2 text-white rounded-lg transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                  copiedGuestId === currentGuest.id 
                    ? 'bg-gradient-to-r from-emerald-400 to-teal-500 shadow-md' 
                    : 'bg-gradient-to-r from-gray-400 to-gray-600 hover:shadow-md'
                }`}
                title={hasChanges ? "Save or cancel changes first" : "Copy invitation link to clipboard"}
              >
                {copiedGuestId === currentGuest.id ? 'Copied!' : 'Copy Invitation Link'}
              </button>
              
              <button
                onClick={() => {
                  onUninvite(currentGuest);
                  onClose();
                }}
                disabled={hasChanges}
                className="w-full px-3 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                title={hasChanges ? "Save or cancel changes first" : "Remove guest from invitation"}
              >
                Uninvite Guest
              </button>
            </div>
          )}
          
          {/* Delete button for all guests */}
          <button
            onClick={() => {
              onDelete(currentGuest.id, currentGuest.name || 'this guest');
              onClose();
            }}
            disabled={hasChanges}
            className="w-full px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            title={hasChanges ? "Save or cancel changes first" : "Permanently delete this guest"}
          >
            Delete Guest
          </button>
        </div>

        {/* Close/Save/Cancel Buttons */}
        <div className="mt-4">
          {hasChanges ? (
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="flex-1 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                Cancel Changes
              </button>
            </div>
          ) : (
            <button
              onClick={onClose}
              className="w-full px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestDetailsModal;